// Monta el cierre por inactividad y muestra el aviso antes de sacar al médico.
//
// El aviso no es cortesía: sacar a alguien sin avisar, en medio de un turno, es
// exactamente el tipo de fricción que hace que la gente deje de usar la app.
import React, { useEffect } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";

import useIdleLogout from "../hooks/useIdleLogout";
import { logout } from "../helpers/authentication-helpers";
import {
  avisarCierrePorInactividad,
  cancelarCierrePendiente,
  cerrarPorInactividad,
} from "../helpers/idle-telemetria";
import { actividadBackend } from "../helpers/get-client";
import { recordarExencion, sesionVencida } from "../helpers/idle-session";
import { useCurrentUser } from "./context/UserContext";

// "Sin actividad" no puede ser solo mouse y teclado: en Turno hay dictados y
// generaciones del LLM que corren minutos sin que nadie toque nada, y sacar al
// médico en la mitad de una epicrisis sería peor que no tener la protección.
//
// Se mira el tráfico REAL contra el backend, no un flag que alguien tenga que
// acordarse de levantar. Solo cuentan las escrituras: los GET quedan afuera a
// propósito porque la app tiene pollers en loop, y si contaran, el timeout no
// se cumpliría nunca.
//
// Queda además el escape hatch `data-smartdx-busy` en el body, para trabajo que
// no pasa por nuestro backend (por ejemplo el streaming de audio a Deepgram).
const VENTANA_TRABAJO_MS = 90 * 1000;

const hayTrabajoEnCurso = () => {
  try {
    if (document.body.dataset.smartdxBusy === "1") return true;
  } catch {
    /* sin DOM */
  }
  const { enVuelo, desdeUltima } = actividadBackend();
  return enVuelo > 0 || desdeUltima < VENTANA_TRABAJO_MS;
};

const IdleLogoutGuard = () => {
  const { currentUser } = useCurrentUser();
  // Hay quien trabaja siempre en los mismos computadores (Hans, en Clínica
  // Alemana): ahí el cierre no compra seguridad y la fricción es diaria. El
  // flag se prende por usuario desde /admin/users.
  const exento = !!currentUser?.idle_logout_exempt;

  // Se guarda en el navegador porque el chequeo de arranque
  // (helpers/idle-session) corre antes de que vuelva /api/user/me/: sin este
  // cache, al exento lo mandaríamos al login antes de saber que lo era.
  //
  // Y por eso mismo hay que REVALIDAR acá, con el dato del backend en la mano:
  // un cache rancio (quedó el "1" del médico exento que usó este computador
  // antes) le regalaba al siguiente una sesión entera. `ignorarExencion` no es
  // opcional: sin eso la revalidación consulta el mismo cache que viene a
  // invalidar, no cierra nunca, y encima el hook renueva la marca y borra la
  // evidencia. Primero se pisa el cache, después se pregunta.
  //
  // Corre ANTES que los efectos de useIdleLogout, que es quien vuelve a marcar
  // actividad: si se invirtiera el orden, la marca fresca taparía el vencimiento.
  useEffect(() => {
    if (!currentUser) return;
    // Llegó la respuesta del backend: si es exento, se cancela el cierre que el
    // arranque dejó agendado por no saber quién era.
    if (exento) cancelarCierrePendiente();
    recordarExencion(exento);
    if (!exento && sesionVencida({ ignorarExencion: true })) {
      // Mismo camino que el arranque: en una ruta pública la sesión muere
      // pero NO se redirige, o el enlace del correo se pierde.
      cerrarPorInactividad("arranque");
    }
  }, [currentUser, exento]);

  const { avisando, seguirConectado } = useIdleLogout({
    enabled: !!currentUser && !exento,
    // Se deja constancia ANTES de cerrar: despues de logout() no hay token con
    // que reportar nada, y sin esto "el cierre por inactividad anda" solo se
    // puede afirmar desde una prueba, nunca desde el uso real.
    onLogout: (motivo) => {
      avisarCierrePorInactividad(motivo);
      logout();
    },
    isBusy: hayTrabajoEnCurso,
  });

  // El aviso tambien se reporta: la distancia entre cuantos avisos salen y
  // cuantos cierres ocurren dice cuantos medicos alcanzan a apretar "Seguir
  // conectado", que es la medida de si el timeout molesta o no.
  useEffect(() => {
    if (avisando) avisarCierrePorInactividad("aviso");
  }, [avisando]);

  if (!avisando) return null;

  return (
    <Dialog
      open
      onClose={seguirConectado}
      maxWidth="xs"
      fullWidth
      // Por encima de TODO lo de la app. El Dialog de MUI sale en z-index 1300 y
      // en el Turno conviven overlays hasta 20000 (el gate de Términos): el
      // aviso estaba en el DOM pero tapado, así que al médico lo sacaban sin
      // haber visto nada. Un aviso invisible es peor que no tenerlo: promete una
      // cortesía que no ocurre.
      sx={{ zIndex: 21000 }}
      PaperProps={{ sx: { borderRadius: 3, backgroundColor: "var(--bg-elevated)", backgroundImage: "none" } }}
    >
      <DialogTitle sx={{ fontWeight: 700 }}>Sigues ahí?</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13.5, color: "text.secondary", lineHeight: 1.55 }}>
          Por seguridad cerramos la sesión en los computadores que quedan sin uso.
          En tu teléfono no pasa.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={logout} sx={{ textTransform: "none", color: "text.muted" }}>
          Cerrar sesión
        </Button>
        <Button onClick={seguirConectado} variant="contained" disableElevation sx={{ textTransform: "none" }}>
          Seguir conectado
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IdleLogoutGuard;
